import { ROLE_TYPES } from 'app/shared/components/loanApplication/RoleTag/RoleTag.constants';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  borrowerContactType,
  loanSummaryContactsTitles,
  loanSummaryIgnoreList,
} from 'constants/contacts';
import { LoanPipelineColumnValueType } from 'constants/loanPipelines';
import { normalizeViewPhoneNumber } from 'utils/data';
import { getPersonFullName, getPersonInitials } from 'utils/loanApplication';
import { invertSortDirection, parseColumnSortKey } from 'utils/pipelines';

import { selectNormalizedPipelinesColumnDefinitions } from 'store/system/selectors';

const selectLoanPipelineEntities = ({ loanPipelines }) => loanPipelines.loanPipelineEntities;

const selectLoanPipelineEntitiesData = createSelector(
  selectLoanPipelineEntities,
  ({ data }) => data,
);

export const selectLoanPipelineSummaryData = createSelector(
  selectLoanPipelineEntities,
  ({ summaryData }) => summaryData,
);

export const selectLoanPipelineSummaryDataSummary = createSelector(
  selectLoanPipelineSummaryData,
  summaryData => summaryData.summary,
);

export const selectLoanPipelineSummaryDataAusFindings = createSelector(
  selectLoanPipelineSummaryData,
  summaryData => summaryData.ausFindings,
);

export const selectLoanPipelineSummaryDataTasks = createSelector(
  selectLoanPipelineSummaryData,
  summaryData => summaryData.tasks,
);

export const selectLoanPipelineSummaryDataContacts = createSelector(
  selectLoanPipelineSummaryData,
  ({ contacts }) => {
    if (!contacts) {
      return [];
    }

    const sortedContacts = contacts.reduce((acc, contact) => {
      const {
        emailAddress,
        firstName,
        lastName,
        phoneNumber,
        externalContactType,
        roleName,
        type,
      } = contact;

      if (loanSummaryIgnoreList.includes(externalContactType)) {
        return acc;
      }

      const getRoleType = () => {
        if (borrowerContactType.includes(externalContactType)) {
          return ROLE_TYPES.BORROWER;
        }

        if (type === 'External') {
          return ROLE_TYPES.NON_BORROWER;
        }

        return ROLE_TYPES.INTERNAL;
      };

      const role = {
        roleName: loanSummaryContactsTitles[externalContactType] ?? roleName ?? externalContactType,
        roleType: getRoleType(externalContactType),
      };

      acc.push({
        emailAddress,
        fullName: getPersonFullName({ firstName, lastName }),
        initials: getPersonInitials({ firstName, lastName }),
        phoneNumber: normalizeViewPhoneNumber(phoneNumber),
        roles: [role],
      });

      return acc;
    }, []);

    return _.orderBy(
      sortedContacts,
      [
        contact => ['borrower', 'nonBorrower', 'internal'].indexOf(contact.roles[0]?.roleType),
        'roleName',
      ],
      ['asc', 'asc'],
    );
  },
);

export const selectLoanPipelineSummaryDataConditions = createSelector(
  selectLoanPipelineSummaryData,
  summaryData => summaryData.conditions,
);

export const selectLoanPipelineSummaryDataProgress = createSelector(
  selectLoanPipelineSummaryData,
  summaryData => summaryData.progress,
);

const selectLoanPipelineEntitiesList = createSelector(
  selectLoanPipelineEntitiesData,
  Object.values,
);

const selectLoanPipelineEntitiesMetadata = createSelector(
  selectLoanPipelineEntities,
  ({ metadata }) => metadata,
);

const selectLoanPipelineEntitiesPartialLoadedIds = createSelector(
  selectLoanPipelineEntities,
  ({ partialLoadedEntriesIds }) => partialLoadedEntriesIds,
);

export const selectIsLoanPipelineEntityPartialLoadedById = memoizeOne(id =>
  createSelector(
    selectLoanPipelineEntitiesPartialLoadedIds,
    partialLoadedEntriesIds => !!partialLoadedEntriesIds[id],
  ),
);

const selectLoanPipelineEntitiesSortOrder = createSelector(
  selectLoanPipelineEntitiesMetadata,
  ({ _sortOrder }) => _sortOrder,
);

export const selectDynamicSortedLoanPipelineEntities = memoizeOne(representationViewType =>
  createSelector(
    [
      selectLoanPipelineEntitiesList,
      selectLoanPipelineEntitiesSortOrder,
      selectNormalizedPipelinesColumnDefinitions,
    ],
    (entityData, sortOrder, columnsDefinitions) => {
      // Duplication of BE sorting for correct insertion index
      if (!sortOrder || !representationViewType) {
        return entityData;
      }

      const { id, direction } = sortOrder;
      const { definitionId, isRelativeDays } = parseColumnSortKey(id);
      const effectiveDirection = isRelativeDays ? invertSortDirection(direction) : direction;
      const { valueType } = columnsDefinitions[representationViewType]?.[definitionId] || {};

      return _.orderBy(
        entityData,
        [
          item => {
            const value = item[definitionId];

            switch (valueType) {
              case LoanPipelineColumnValueType.DATE_TIME:
                return new Date(value);

              case LoanPipelineColumnValueType.DECIMAL:
              case LoanPipelineColumnValueType.INTEGER:
                return value;

              default:
                return _.toLower(value);
            }
          },
        ],
        [effectiveDirection],
      );
    },
  ),
);
